/**
 * Interrupters component will be a card
 *
 * @module views/components/Interrupters
 * @memberof -Common
 */

import './Interrupters.scss';

import React, { useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import PaletteInterrupters from 'web-palette/dist/components/Interrupters/Interrupters';

import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { getExternalAction } from '@ulta/core/utils/getExternalAction/getExternalAction';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';

import CopyToClipboard from '@ulta/components/CopyToClipboard/CopyToClipboard';

import * as utils from './Interrupters';

/**
  * Represents an Interrupters component
  * @method
  * @param {InterruptersProps} props - React properties passed from composition
  * @returns PalInterrupters
  */
const Interrupters = ( props ) => {
  const { breakpoint } = useDeviceInflection();
  const PalInterrupters = PaletteInterrupters.default || PaletteInterrupters;
  const interruptersRef = useRef( null );

  const interruptersProps = useMemo( utils.composeInterruptersProps(
    { props, breakpoint },
    { getUltaAssetHtml, getExternalAction } ),
  [props, breakpoint] );

  useIntersectionProcessor( {
    ...props,
    componentRef: interruptersRef
  } );

  return (
    <div
      className='Interrupters'
      ref={ interruptersRef }
    >
      <PalInterrupters
        { ...interruptersProps }
      />
    </div>
  );
};

/**
 * Helper function to format media and actions based on provided data and methods.
 *
 * @param {Object} props - The incoming properties to be formatted for rendering.
 * @param {Object} props.breakpoint - The breakpoint object to determine device type, allowing conditional formatting.
 * @param {string} [props.image] - The desktop image source URL.
 * @param {string} [props.mobileImage] - The mobile image source URL.
 * @param {string} [props.tabletImage] - The tablet image source URL.
 * @param {Object} [props.actionGroups] - Action groups containing external actions; used to format actions if available.
 * @param {Function} getUltaAssetHtml - A function for formatting image media based on device type and image sources.
 * @param {Function} getExternalAction - A function for formatting external actions based on `actionGroups`.
 * @returns {Object} An object containing all original `props`, with additional formatted media and action properties:
 * - `externalMedia` (formatted media, or `null` if no media is provided)
 * - `externalAction` (formatted action, or `null` if no actions are available).
 */
export const composeInterruptersProps = ( data, methods ) => () => {
  const { breakpoint, props } = data || {};
  const { getUltaAssetHtml, getExternalAction } = methods || {};

  if( !breakpoint || !props ){
    return {};
  }

  const {
    image,
    mobileImage,
    tabletImage,
    actionGroups,
    couponDetails,
    couponAction,
    ...interruptersProps
  } = props;

  // Breakpoint settings for incoming media
  const isMobile = breakpoint?.isSmallDevice();
  const isDesktop = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'LG' ||
    breakpoint.CURRENT_BREAKPOINT === 'XL' );
  const isTablet = breakpoint && breakpoint.CURRENT_BREAKPOINT === 'MD';

  // Image check for formatters
  const hasImage = image || mobileImage || tabletImage;

  // Action check for formatters
  const hasAction = actionGroups && actionGroups.actions[0]?.label;
  const hasCouponAction = couponAction?.label && props?.variant === 'coupon';

  // Objects for external media and actions coming from Amplience
  let formattedMedia = null;
  let formattedAction = null;
  let copyActionEl = null;

  // Formatters for incoming media and actions
  if( hasImage ){
    formattedMedia = getUltaAssetHtml( {
      image: image,
      mobileImage: mobileImage,
      tabletImage: tabletImage,
      isMobile: isMobile,
      isDesktop: isDesktop,
      isTablet: isTablet
    } );
  }

  if( hasAction && !hasCouponAction ){
    formattedAction = getExternalAction( { actionGroups } );
  }

  if( hasCouponAction ){
    const couponCode = couponDetails?.couponCode;
    couponAction.copyText = couponCode;

    const copyElProps = {
      couponCode,
      copiedToClipLabel: couponDetails?.couponAppliedLabel,
      copyToClipAction: couponAction
    };

    copyActionEl = (
      <CopyToClipboard
        { ...copyElProps }
        buttonProps={ { variant: 'secondary', size: 'tiny' } }
      />
    );
  }

  return {
    ...interruptersProps,
    externalMedia: formattedMedia,
    externalAction: formattedAction,
    externalCouponAction: copyActionEl
  };
};

/**
 * Property type definitions
 * @typedef InterruptersProps
 * @type {object}
 * @property {object} actionGroups - actionGroups object
 * @property {string} backgroundColor - string value that sets the background color of the Interrupters content
 * @property {string} bodyCopy - string value that sets the bodyCopy text
 * @property {string} cardAccessibilityLabel - string that sets the aria-label
 * @property {object} couponDetails - couponDetails object
 * @property {object} externalAction - external action object
 * @property {string} headline - string value that sets the headline text
 * @property {string} headlineTag - string value that sets the HTML heading level tag
 * @property {string} image - url src for the desktop image
 * @property {string} variant - string value that sets the variants of Interrupters
 */
export const propTypes = {
  /** Allows passing an object to the actionGroups prop */
  actionGroups: PropTypes.object,
  /** Sets the background color */
  backgroundColor: PropTypes.string,
  /** Sets the bodyCopy text */
  bodyCopy: PropTypes.string,
  /** String that sets the aria-label for Interrupters */
  cardAccessibilityLabel: PropTypes.string,
  /** Sets the coupon details action */
  couponDetails: PropTypes.object,
  /**
  * Sets the primary action from an external
  * source that contains business logic
  */
  externalAction: PropTypes.object,
  /** Sets the headline text */
  headline: PropTypes.string,
  /** Sets the tag to be used for headline */
  headlineTag: PropTypes.string,
  /** Sets the image to be used */
  image: PropTypes.string,
  /** Sets the variant of the Interrupters */
  variant: PropTypes.string
};

export default Interrupters;